import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Progress, Spin, Upload, UploadProps, message } from 'antd';
import {
  AuditOutlined,
  InboxOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import PublicActionPage from '../components/PublicActionPage';
import KofiSupportPanel from '../support/KofiSupportPanel';
import ToolRunAllowance from '../support/ToolRunAllowance';
import { KofiRequiredError } from '../support/kofi.service';
import { useToolGate } from '../support/useToolGate';
import {
  AtsReport,
  JobService,
  ResumeImproveJobStatus,
  ResumeImproveProgress,
} from './services/job.service';

const progressPercentByStatus: Record<ResumeImproveJobStatus, number> = {
  QUEUED: 15,
  PROCESSING: 55,
  COMPLETED: 100,
  FAILED: 0,
};

const AtsCheck: React.FC = () => {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId')?.trim() || '';
  const [jobDescription, setJobDescription] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fileSpinner, setFileSpinner] = useState(false);
  const [progressMessage, setProgressMessage] = useState(
    'Checking your resume...'
  );
  const [progressPercent, setProgressPercent] = useState(0);
  const [processError, setProcessError] = useState<string | null>(null);
  const [report, setReport] = useState<AtsReport | null>(null);
  const { status, kofiRequired, setKofiRequired, refresh, onRedeemed } =
    useToolGate();
  const { Dragger } = Upload;

  const resetResults = () => {
    setReport(null);
    setProcessError(null);
    setFileSpinner(false);
    setProgressPercent(0);
    setProgressMessage('Checking your resume...');
  };

  const processFile = async (resume: File) => {
    setPendingFile(resume);
    setProcessError(null);
    setKofiRequired(null);
    setReport(null);
    try {
      setFileSpinner(true);
      setProgressPercent(10);
      setProgressMessage('Uploading your resume...');
      const result = await JobService.CheckAts(
        resume,
        {
          jobId: jobId || undefined,
          jobDescription: jobId
            ? undefined
            : jobDescription.trim() || undefined,
        },
        (progress: ResumeImproveProgress) => {
          setProgressMessage(progress.message);
          setProgressPercent(progressPercentByStatus[progress.status] ?? 10);
        }
      );
      setReport(result);
      message.success('Your ATS report is ready.');
      void refresh();
    } catch (error: unknown) {
      if (error instanceof KofiRequiredError) {
        setKofiRequired(error);
        return;
      }
      const serverMessage = error instanceof Error ? error.message.trim() : '';
      setProcessError(
        serverMessage && !/^\d{3}\s*-/.test(serverMessage)
          ? serverMessage
          : "We couldn't check that resume. Try a text-based PDF or DOCX."
      );
    } finally {
      setFileSpinner(false);
    }
  };

  const uploadProps: UploadProps = {
    multiple: false,
    maxCount: 1,
    accept: '.pdf,.docx',
    showUploadList: false,
    disabled: fileSpinner,
    beforeUpload(file) {
      const isPDF = file.type === 'application/pdf';
      const isDOCX =
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      if (!isPDF && !isDOCX) {
        message.error('Only PDF or DOCX files are allowed.');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    async onChange(info) {
      if (info.file && info.file.status !== 'removed') {
        const rawFile = (info.file.originFileObj || info.file) as File;
        await processFile(rawFile);
      }
    },
  };

  return (
    <PublicActionPage
      eyebrow="ATS check"
      title="See how a parser reads your resume"
      description="We score formatting, readability, and keyword alignment when you add a job. This is not a vendor ATS and does not predict a hiring decision."
      icon={<AuditOutlined />}
      steps={[
        {
          title: 'Upload a PDF or DOCX',
          detail: 'Text-based files parse best.',
        },
        {
          title: 'Add a job if you want keyword alignment',
          detail: 'Paste a description, or open this page from a job.',
        },
        {
          title: 'Review the findings',
          detail: 'Each item includes a concrete fix.',
        },
      ]}
      asideNote="Your file stays only long enough to build the report."
      formEyebrow="Resume check"
      formTitle="Drop your resume here"
      formDescription="The check starts as soon as the file is accepted."
      footer={
        <p className="jo-body-muted mb-0 text-center text-sm">
          Want a rewrite?{' '}
          <Link to="/improve-your-resume" className="public-inline-link">
            Improve your resume
          </Link>
          .
        </p>
      }
    >
      <div className="mx-auto w-full max-w-xl">
        <ToolRunAllowance status={status} />
        {!report && (
          <>
            {jobId ? (
              <p className="jo-body-subtle mb-3 text-sm" role="status">
                Keyword alignment will use the job you opened.
              </p>
            ) : (
              <label className="public-form-field mb-4 block">
                <span className="public-form-label">
                  Job description (optional)
                </span>
                <textarea
                  className="mt-2 w-full rounded-lg border border-[var(--jo-border)] bg-transparent p-3 text-sm"
                  rows={5}
                  maxLength={8000}
                  value={jobDescription}
                  onChange={(event) => setJobDescription(event.target.value)}
                  placeholder="Paste the posting if you want keyword alignment."
                />
              </label>
            )}
            <Dragger
              {...uploadProps}
              className="public-upload-zone flex min-h-[180px] flex-col justify-center rounded-lg"
              aria-label="Resume file"
            >
              {fileSpinner ? (
                <div
                  className="flex w-full flex-col items-center gap-4 px-4"
                  role="status"
                  aria-live="polite"
                >
                  <Spin />
                  <Progress
                    percent={progressPercent}
                    status="active"
                    showInfo={false}
                    className="w-full max-w-sm"
                  />
                  <p className="jo-body-subtle mb-0 text-center text-sm">
                    {progressMessage}
                  </p>
                </div>
              ) : (
                <>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ fontSize: '36px' }} />
                  </p>
                  <p className="ant-upload-text">Drag & drop or browse</p>
                  <p className="ant-upload-hint">PDF or DOCX</p>
                </>
              )}
            </Dragger>
          </>
        )}

        {kofiRequired && !fileSpinner && (
          <KofiSupportPanel
            required={kofiRequired}
            onRedeemed={(next) => {
              onRedeemed(next);
              if (pendingFile) {
                void processFile(pendingFile);
              }
            }}
          />
        )}

        {processError && !fileSpinner && (
          <div
            className="public-result-panel public-result-panel--error mt-4"
            role="alert"
          >
            <p className="mb-3 text-sm font-semibold">{processError}</p>
            <Button
              className="public-button-secondary"
              icon={<ReloadOutlined />}
              onClick={resetResults}
            >
              Try another file
            </Button>
          </div>
        )}

        {report && <AtsReportView report={report} onReset={resetResults} />}
      </div>
    </PublicActionPage>
  );
};

function AtsReportView({
  report,
  onReset,
}: {
  report: AtsReport;
  onReset: () => void;
}) {
  const groups = ['FORMATTING', 'READABILITY', 'KEYWORDS'];
  return (
    <div
      className="public-result-panel mt-2 text-left"
      role="status"
      aria-live="polite"
    >
      <p className="jo-heading mb-1 text-3xl font-black">
        {report.overallScore}
      </p>
      <p className="jo-body-muted mb-4 text-sm">Overall ATS compatibility</p>
      <ul className="mb-4 grid grid-cols-3 gap-2 p-0 text-sm">
        <li>Formatting {report.formattingScore}</li>
        <li>Readability {report.readabilityScore}</li>
        <li>
          {report.keywordsEvaluated
            ? `Keywords ${report.keywordScore ?? 0}`
            : 'Keywords not evaluated'}
        </li>
      </ul>
      {groups.map((category) => {
        const items = report.findings.filter(
          (finding) => finding.category === category
        );
        if (items.length === 0) {
          return null;
        }
        return (
          <section key={category} className="mb-4">
            <h3 className="jo-heading mb-2 text-sm font-black">{category}</h3>
            <ul className="m-0 list-none space-y-3 p-0">
              {items.map((item, index) => (
                <li
                  key={`${category}-${index}`}
                  className="public-improvement-item"
                >
                  <p className="mb-1 text-sm font-semibold">{item.message}</p>
                  {item.fix && (
                    <p className="jo-body-subtle mb-0 text-sm">{item.fix}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      {report.keywordsEvaluated && (
        <section className="mb-4">
          <h3 className="jo-heading mb-2 text-sm font-black">Keywords</h3>
          <p className="jo-body-subtle text-sm">
            Matched:{' '}
            {report.matchedKeywords.length
              ? report.matchedKeywords.join(', ')
              : 'none'}
          </p>
          <p className="jo-body-subtle text-sm">
            Missing:{' '}
            {report.missingKeywords.length
              ? report.missingKeywords.join(', ')
              : 'none'}
          </p>
        </section>
      )}
      <p className="jo-body-muted text-sm">{report.disclaimer}</p>
      <Button
        className="public-button-secondary mt-4"
        icon={<ReloadOutlined />}
        onClick={onReset}
      >
        Check another
      </Button>
    </div>
  );
}

export default AtsCheck;
